import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle, XCircle, AlertCircle, Lock, Eye, EyeOff, Phone, User,
  Shield, Stethoscope, UserCheck, FlaskConical, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/lib/api';
import { toast } from 'sonner';

const ROLE_CONFIG = {
  admin: { name: 'Administrator', icon: Shield, color: 'bg-purple-100 text-purple-700' },
  doctor: { name: 'Doctor', icon: Stethoscope, color: 'bg-blue-100 text-blue-700' },
  reception: { name: 'Receptionist', icon: UserCheck, color: 'bg-emerald-100 text-emerald-700' },
  lab_tech: { name: 'Lab Technician', icon: FlaskConical, color: 'bg-amber-100 text-amber-700' }
};

const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 12 characters', test: (p) => p.length >= 12 },
  { id: 'uppercase', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lowercase', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'One number', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'One special character (!@#$%^&*)', test: (p) => /[!@#$%^&*(),.?":{}|<>]/.test(p) }
];

export default function Onboarding() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [validating, setValidating] = useState(true);
  const [validationResult, setValidationResult] = useState(null);
  const [invitation, setInvitation] = useState(null);

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
    phone: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidationResult({ valid: false, message: 'No invitation token provided' });
      setValidating(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      const response = await api.get(`/onboard/validate/${token}`);
      setValidationResult(response.data);
      if (response.data.valid && response.data.invitation) {
        setInvitation(response.data.invitation);
      }
    } catch (error) {
      console.error('Token validation error:', error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          'Failed to validate invitation. Please try again or contact support.';
      setValidationResult({ valid: false, message: errorMessage });
    } finally {
      setValidating(false);
    }
  };

  const getPasswordStrength = () => {
    const passed = PASSWORD_REQUIREMENTS.filter(req => req.test(formData.password)).length;
    if (passed === 0) return { label: '', color: '' };
    if (passed <= 2) return { label: 'Weak', color: 'text-rose-500' };
    if (passed <= 4) return { label: 'Medium', color: 'text-amber-500' };
    return { label: 'Strong', color: 'text-emerald-500' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate password requirements
    const allPassed = PASSWORD_REQUIREMENTS.every(req => req.test(formData.password));
    if (!allPassed) {
      toast.error('Please meet all password requirements');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post('/onboard/complete', {
        token,
        password: formData.password,
        phone: formData.phone || null
      });

      toast.success(response.data.message || 'Account created successfully!');

      // Redirect to login after short delay
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Account created successfully! Please log in.',
            email: response.data.email 
          } 
        });
      }, 1500);
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle different error formats
      let errorMessage = 'Failed to complete registration';
      
      if (error.response?.data) {
        const data = error.response.data;
        // Handle Pydantic validation errors (422)
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            // Pydantic v2 format: [{loc: [...], msg: "...", type: "..."}]
            errorMessage = data.detail.map(err => err.msg || err.message).join('; ');
          } else if (typeof data.detail === 'string') {
            errorMessage = data.detail;
          } else if (typeof data.detail === 'object' && data.detail.msg) {
            errorMessage = data.detail.msg;
          }
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 animate-spin text-[#4A238F] mx-auto mb-4" />
            <p className="text-slate-600">Validating your invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state or any error
  if (!validationResult?.valid || !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Card className="w-full max-w-md">
            <CardContent className="p-8 text-center">
              <div className="h-16 w-16 rounded-full bg-rose-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="h-8 w-8 text-rose-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Invalid Invitation</h2>
              <p className="text-slate-600 mb-6">{validationResult?.message || 'This invitation link is not valid or has expired.'}</p>
              <Button
                onClick={() => navigate('/login')}
                className="bg-[#4A238F] hover:bg-[#3A1B72] text-white"
              >
                Go to Login
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    );
  }

  const roleConfig = ROLE_CONFIG[invitation?.role] || { name: invitation?.role || 'User', icon: User, color: 'bg-slate-100 text-slate-700' };
  const RoleIcon = roleConfig.icon || User;
  const passwordStrength = getPasswordStrength();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-white flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg"
      >
        {/* Header */}
        <div className="text-center mb-6">
          <img
            src="/logo-complete.png"
            alt="Zesta Healthcare"
            className="h-16 w-16 mx-auto mb-3 bg-white rounded-xl p-2 shadow-sm"
          />
          <h1 className="text-2xl font-bold text-slate-900">Zesta Healthcare</h1>
          <p className="text-slate-500 text-sm italic">Paperless Productive Practice</p>
        </div>

        <Card className="border-2 border-purple-100 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className={`h-14 w-14 rounded-full ${roleConfig.color} flex items-center justify-center mx-auto mb-3`}>
              <RoleIcon className="h-7 w-7" />
            </div>
            <CardTitle className="text-xl">Complete Your Registration</CardTitle>
            <CardDescription>
              Welcome, <strong>{invitation?.full_name}</strong>!<br />
              You've been invited as a <Badge className={roleConfig.color}>{roleConfig.name}</Badge>
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-4">
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Email (Read-only) */}
              <div className="space-y-2">
                <Label>Email Address</Label>
                <Input
                  value={invitation?.email || ''}
                  disabled
                  className="bg-slate-50"
                />
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number (Optional)</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+1 (555) 000-0000"
                    className="pl-10"
                    data-testid="onboard-phone-input"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Create Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Create a strong password"
                    className="pl-10 pr-10"
                    required
                    data-testid="onboard-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {formData.password && (
                  <p className={`text-xs font-medium ${passwordStrength.color}`}>
                    Password strength: {passwordStrength.label}
                  </p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-slate-50 rounded-lg p-3 space-y-1.5">
                <p className="text-xs font-medium text-slate-600 mb-2">Password Requirements:</p>
                {PASSWORD_REQUIREMENTS.map((req) => {
                  const passed = req.test(formData.password);
                  return (
                    <div key={req.id} className="flex items-center gap-2">
                      {passed ? (
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                      ) : (
                        <div className="h-3.5 w-3.5 rounded-full border border-slate-300" />
                      )}
                      <span className={`text-xs ${passed ? 'text-emerald-600' : 'text-slate-500'}`}>
                        {req.label}
                      </span>
                    </div>
                  );
                })}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    placeholder="Confirm your password"
                    className="pl-10 pr-10"
                    required
                    data-testid="onboard-confirm-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                  <p className="text-xs text-rose-500">Passwords do not match</p>
                )}
                {formData.confirmPassword && formData.password === formData.confirmPassword && formData.password && (
                  <p className="text-xs text-emerald-500 flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" /> Passwords match
                  </p>
                )}
              </div>

              <Alert className="bg-purple-50 border-purple-200">
                <AlertCircle className="h-4 w-4 text-purple-600" />
                <AlertDescription className="text-purple-700 text-xs">
                  By completing registration, you agree to follow Zesta Healthcare's security policies and HIPAA compliance requirements.
                </AlertDescription>
              </Alert>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white h-11"
                data-testid="complete-registration-button"
              >
                {submitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Account...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Complete Registration
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-xs mt-4">
          Already have an account?{' '}
          <button
            onClick={() => navigate('/login')}
            className="text-[#4A238F] hover:underline font-medium"
          >
            Sign in here
          </button>
        </p>
      </motion.div>
    </div>
  );
}
